import type { GeoPoint } from './geo'
/**
 * Ownership — who owns the company, and how they came to.
 *
 * From the 13 Aug design round, under Internal; the ledger below is the 14 Aug
 * round. Three records: a shareholder, the issuance their shares were bought in,
 * and a transfer moving shares between two of them. Nothing that can be computed
 * is stored — status, ownership percentage, money raised, shares outstanding are
 * all derived at read time, so no figure here can disagree with another.
 *
 * ## The cap table is a projection, not a number somebody keeps up to date
 *
 * A holding is never written down. It is the sum of three ledgers:
 *
 *   purchases          shares the company issued to this person
 * + exercised options  shares that came from a grant being exercised
 * - settled transfers  sold to somebody else
 * + settled transfers  bought from somebody else
 *
 * The alternative — a mutable `shares` field the screen keeps in step — has one
 * failure mode and it is the expensive one: the field and the ledger disagree,
 * and there is no way to tell which is right. A projection can be wrong; it
 * cannot be *inconsistent*, and it is re-derivable from the documents at any
 * later date, which is what a share register is for.
 *
 * ## Two rules that make the arithmetic hold
 *
 * **A transfer never creates shares.** Only an issuance does that, and — since
 * 14 Aug — an option being exercised. A transfer between two holders leaves
 * `sharesOutstanding` alone entirely; the one thing that lowers it is a company
 * buy-back, where the shares leave the register and everybody else's percentage
 * rises without anybody buying anything.
 *
 * **Only settled transfers move anything.** A pending transfer is a claim: the
 * board has not consented, the form is unsigned, the money has not moved. Its
 * shares stay with the seller and count in the seller's holding, percentage and
 * every total until the day it settles.
 *
 * ## The personnummer, and the three answers behind it
 *
 * Held back until 13 Aug, when Bjarte settled all three:
 *
 *   1. **Purpose** — it is part of the aksjeeierbok. A share register identifies
 *      its holders; that is what the register is for.
 *   2. **Who may read it** — admins. `skidaCrmShareholders` is admin-only in
 *      `firestore.rules`, both read and write, so the field needs no home of its
 *      own: there is no role that can read the document but not the field.
 *   3. **Retention** — historical shareholders are kept, personnummer included,
 *      because a register that forgets who held shares last year is not a
 *      register. So a holder with shares against them cannot be deleted from the
 *      screen; see `OwnershipWorkspace`.
 *
 * Stored as eleven digits and shown only on the record itself. It was a column
 * on the table until 14 Aug, masked to the last four; the design took it off
 * list views entirely, and a masked column only ever said "on file", which the
 * record says better.
 */

/** A funding round. Collection: `skidaCrmIssuances`. */
export interface Issuance {
  id: string
  name: string
  /** ISO date (YYYY-MM-DD). */
  date: string
  /** Price per share in kroner, as agreed in that round. */
  pricePerShare: number | null
  /**
   * Whether the round is finished or still taking subscriptions.
   *
   * A **completed** round has issued its shares: they are purchases on the
   * holders, they count towards outstanding, and the percentages are computed
   * from them. An **ongoing** round has interest and signed forms against it and
   * has issued nothing — a subscription is a promise, and counting it would
   * dilute every existing holder on paper before a krone arrived.
   *
   * Absent means completed, which is what every round in the register was
   * before Emisjon 4 opened.
   */
  status?: IssuanceStatus
  /**
   * The day the capital increase was reported to Foretaksregisteret. ISO date;
   * absent means it has not been.
   *
   * Asked for on 2 Sep 2026, and it is the gate on the aksjeeierbevis: a
   * shareholder can take a certificate for their shares in a round once that
   * round has been reported, and not before.
   *
   * ## Why it is a date and not a boolean
   *
   * The request was for a flag, and a date is a better one — it answers *whether*
   * by being present at all, and it also answers *when*, which a certificate has
   * to print. A capital increase takes effect on registration, so the day it
   * happened is the fact that matters, and a `true` would have meant adding the
   * date a fortnight later anyway. `paidAt` and `paidDate` are the same shape for
   * the same reason.
   *
   * Absent is the safe default in both directions: it is what every round in the
   * register is until somebody says otherwise, and no migration quietly claims a
   * round was reported.
   */
  registeredAt?: string
  /**
   * When the payment request went out, and to how many.
   *
   * Written by `crmSendPaymentRequest` and by nothing else — the card reads it
   * so that "have we asked them yet" is answerable from the register rather
   * than from somebody's sent folder.
   */
  paymentRequestSentAt?: string
  paymentRequestCount?: number
  /** Signed protokoll, tegningsliste, Foretaksregisteret confirmation. */
  attachments?: Attachment[]
  createdAt?: string
  updatedAt?: string
}

/**
 * A file filed against a shareholder or an issuance — decision 54.
 *
 * The record holds the metadata and Storage holds the bytes. **When it was
 * filed is the point**: a share register's value is knowing that the firmaattest
 * arrived on the 14th, not merely that one exists somewhere.
 *
 * `id` is generated here rather than derived from the name, because decision 56
 * matches attachments back by id across a CSV import that replaces the whole
 * register. Two shareholders may both file a `firmaattest.pdf`, and a round trip
 * that matched on filename would hand one of them the other's document.
 */
export interface Attachment {
  id: string
  name: string
  /** Bytes, as reported by the browser at upload. */
  size: number
  /** ISO date — the day it was filed, not the day the file was made. */
  uploadedAt: string
  /** Path in the Storage bucket. The only way back to the bytes. */
  path: string
}

export type IssuanceStatus = 'completed' | 'ongoing'

/**
 * Interest in a round that has not closed.
 *
 * Deliberately not a `SharePurchase`. A purchase is shares that exist; this is
 * somebody saying they want some — registered interest, a signed tegningsblankett,
 * an option they intend to exercise. It carries the source's own words in
 * `note`, because "Registrert interesse 29.05.2026 — Opsjonsavtale" and "Signert
 * tegningsblankett 14.04.2026, kurs kr 461,22" are different commitments and the
 * register should not flatten them into one status pill.
 *
 * Nothing here counts towards holdings, outstanding shares or percentages. It
 * becomes a purchase when the round closes and the money arrives.
 */
export interface Subscription {
  issuanceId: string
  /** Shares the person has said they want. */
  shares: number
  /**
   * What this holder pays, when it is not the round's price.
   *
   * A round is not always one price. Emisjon 4 carries three: kr 663 for
   * ordinary subscribers, kr 461,22 on agreements signed before the round
   * opened, and the strike on shares taken up through the option programme.
   * The company agreed each of them, so each is a fact about that holder rather
   * than about the round.
   *
   * **Absent means the round's price**, which is the ordinary case and keeps
   * every existing record correct without a migration. Present, it wins
   * everywhere money is computed: the amount raised, the payment request, the
   * tegningsliste in the minutes.
   */
  pricePerShare?: number | null
  /** What the source recorded, verbatim. */
  note: string
}

/**
 * Where else a shareholder appears in Skida — computed, not entered.
 *
 * The manual links beside this (decision 48) say who somebody *is*: a person
 * picked a lead contact and that is a fact somebody asserted. This is the
 * opposite — a join on the email address, run over the sales book, the
 * ambassadors, the partners, and the product's own users and Skida PRO
 * memberships, by `scripts/matchShareholders.ts`.
 *
 * It is stored rather than derived at read time, which is the one exception to
 * this file's rule, and the reason is narrow: the join crosses into the product
 * project, where the browser may read very little, and 144 holders against
 * 49 725 users is not a query a screen can run. `matchedAt` says how old the
 * answer is so nobody reads it as live.
 *
 * **Only the email is matched.** Names are not, deliberately: a company that
 * looks like a lead probably is one, and "probably" on the screen that says who
 * owns this company is worse than a blank.
 */
export interface ShareholderMatches {
  lead?: { id: string; name: string; as: 'company' | 'contact'; stage: string }
  ambassador?: { id: string; name: string; status: string }
  partner?: { id: string; name: string }
  /** They have a Skida account; entitlements are the paid ones, if any. */
  appUser?: { uid: string; entitlements: string[] }
  /** They hold a role inside a Skida PRO company. */
  proMember?: { companyId: string; companyName: string; role: string }
  /** ISO timestamp of the run that produced this. */
  matchedAt?: string
}

/** Shares bought by one shareholder in one issuance. */
export interface SharePurchase {
  /** `Issuance.id`. The round is the link; its name is looked up, never copied. */
  issuanceId: string
  shares: number
  /**
   * What this holder pays, when it is not the round's price.
   *
   * A round is not always one price. Emisjon 4 carries three: kr 663 for
   * ordinary subscribers, kr 461,22 on agreements signed before the round
   * opened, and the strike on shares taken up through the option programme.
   * The company agreed each of them, so each is a fact about that holder rather
   * than about the round.
   *
   * **Absent means the round's price**, which is the ordinary case and keeps
   * every existing record correct without a migration. Present, it wins
   * everywhere money is computed: the amount raised, the payment request, the
   * tegningsliste in the minutes.
   */
  pricePerShare?: number | null
  /** ISO date. Usually the issuance's, but a late subscription differs. */
  date: string
  /**
   * What the source said, where a holding came from something worth recording.
   *
   * Carried over when a round closes and a subscription becomes a purchase, so
   * "Signert tegningsblankett 14.04.2026, kurs kr 461,22" survives the moment
   * the shares start existing. A share register is evidence as much as
   * arithmetic, and that sentence is the evidence for a price that differs from
   * the round's.
   */
  note?: string
  /**
   * The day the money for these shares landed. ISO date; absent means unpaid.
   *
   * Added 28 Aug 2026, because a payment request that cannot be reconciled is
   * half a feature: the register knew who had been *asked* and never who had
   * *paid*, so chasing meant reading a bank statement beside a screen and
   * matching names by eye. Thirteen of Emisjon 4's thirty-eight had paid and
   * nothing in the tool knew it.
   *
   * **It records receipt, not entitlement.** The shares already exist — they are
   * a purchase, counted in the holding, the percentage and the outstanding
   * total, and that does not change when the money arrives. This is the
   * company's side of the transaction, and it is deliberately the same shape as
   * `OptionGrant.paidDate`, which has meant the same thing since 14 Aug.
   *
   * Absent is the safe default in both directions: an unpaid purchase is what
   * every existing row is until somebody says otherwise, and nothing is
   * silently marked settled by a migration.
   */
  paidAt?: string
}

/**
 * What a holder actually pays per share in a round.
 *
 * One function, used everywhere money is computed, so a price agreed with one
 * subscriber cannot be honoured on the card and forgotten in the payment
 * request.
 */
export function effectivePrice(
  row: { pricePerShare?: number | null },
  issuance: { pricePerShare: number | null } | undefined,
): number | null {
  return row.pricePerShare ?? issuance?.pricePerShare ?? null
}

/**
 * What became of a grant.
 *
 * `active` is a promise, `exercised` is a capital increase in progress, and
 * `expired` is nothing. The three are kept on one record rather than in three
 * lists because the grant is one thing that changed state, and the record shows
 * the whole split.
 *
 * ## Exercising is a share capital increase, and is recorded as one
 *
 * Decision 16 of the 14 Aug round, and it replaced a status flip. An exercise
 * now carries three things a registry would ask for:
 *
 *   exerciseIssuanceId   the increase it is booked against
 *   exerciseResolution   the board or general meeting that allowed it
 *   paidDate             when the strike price was actually paid
 *
 * The last one is what decides whether the shares exist. **Exercised but unpaid
 * is its own state: recorded, and counted nowhere.** Subscribed-and-unpaid
 * shares are not shares — the money buys them — so counting them would overstate
 * the company's capital and dilute every real holder on the screen against
 * shares nobody has paid for.
 */
export type OptionStatus = 'active' | 'exercised' | 'expired'

export const OPTION_STATES: {
  key: OptionStatus
  label: string
  color: string
  bg: string
}[] = [
  { key: 'active', label: 'Active', color: '#3456da', bg: '#eaefff' },
  { key: 'exercised', label: 'Exercised', color: '#3aa52f', bg: '#eaf7e7' },
  { key: 'expired', label: 'Expired', color: '#8f96a3', bg: '#f1f2f3' },
]

export const optionStateStyle = (s: OptionStatus) =>
  OPTION_STATES.find((x) => x.key === s) ?? OPTION_STATES[0]!

export interface OptionGrant {
  count: number
  /** ISO date. */
  grantDate: string
  /** Strike in kroner. */
  strikePrice: number | null
  /** ISO date, or '' when nobody has set one. */
  expiryDate: string
  status: OptionStatus
  /** Set the day the grant leaves `active`; '' on the other two. */
  exerciseDate: string
  expiredDate: string
  /** `Issuance.id` the exercise is booked against. '' unless exercised. */
  exerciseIssuanceId: string
  /** Board or general-meeting resolution behind the increase. */
  exerciseResolution: string
  /** ISO date the strike price was paid. '' means the shares do not exist yet. */
  paidDate: string
}

/**
 * An exercised grant that has not been paid for.
 *
 * Recorded everywhere, counted nowhere. Kept as its own predicate because three
 * separate places have to agree about it: the holding, the record's own summary,
 * and the pill on the row.
 */
export const isUnpaidExercise = (o: OptionGrant): boolean =>
  o.status === 'exercised' && !o.paidDate

/**
 * Take `count` options off grant `i` so part of an agreement can be exercised.
 *
 * Splits rather than adding an "exercised so far" field to the grant: every
 * total, the minutes, the receipt and the certificate already read one grant as
 * one block of options in one state, and a split keeps that true. Grant `i`
 * becomes the `count` that will be exercised; the rest is appended as a new
 * active grant on the same terms. Appended, never inserted — receipts find an
 * exercise by its position in the list, so no existing grant may move.
 *
 * Returns the list unchanged when `count` is the whole grant, and null when it
 * is not a whole number between 1 and the grant's count.
 */
export function splitGrant(
  grants: OptionGrant[],
  i: number,
  count: number,
): OptionGrant[] | null {
  const g = grants[i]
  if (!g || !Number.isInteger(count) || count < 1 || count > g.count) return null
  if (count === g.count) return grants
  return [
    ...grants.map((x, n) => (n === i ? { ...x, count } : x)),
    { ...g, count: g.count - count },
  ]
}

/** What the pill on a grant row says, which the status alone cannot answer. */
export function optionStateLabel(o: OptionGrant): { label: string; color: string; bg: string } {
  if (isUnpaidExercise(o)) {
    return { label: 'Exercised — unpaid', color: '#7b5200', bg: '#fff3d8' }
  }
  return optionStateStyle(o.status)
}

/**
 * Shares changing owner. Collection: `skidaCrmTransfers`.
 *
 * The secondary market: a founder selling down, an angel buying in, an employee
 * cashing out. Distinct from an issuance in the only way that matters — the
 * company is not a party to it and no new share exists afterwards.
 */
export interface ShareTransfer {
  id: string
  /** ISO date the shares changed hands, or were agreed to. */
  date: string
  /** `Shareholder.id` of the seller. */
  fromId: string
  /**
   * `Shareholder.id` of the buyer — created first when they are new, and empty
   * on a buy-back, where the company is the buyer and holds nothing.
   */
  toId: string
  /**
   * The company bought the shares back, and they leave the register.
   *
   * The only movement in this file that lowers `sharesOutstanding`. Kept as a
   * flag on the transfer rather than as a shareholder record for Skida AS: the
   * company is not a shareholder in its own register, and giving it a row would
   * put treasury shares into every percentage on the screen.
   */
  buyback: boolean
  shares: number
  /** Kroner per share. The last settled one is the de-facto secondary price. */
  pricePerShare: number | null
  /** False until the board has consented and the form is signed. */
  settled: boolean
  /** Board consent, share transfer form, pre-emption waiver. */
  note: string
  createdAt?: string
  updatedAt?: string
}

export const EMPTY_TRANSFER: Omit<ShareTransfer, 'id'> = {
  date: '',
  fromId: '',
  toId: '',
  buyback: false,
  shares: 0,
  pricePerShare: null,
  settled: false,
  note: '',
}

/**
 * What kind of legal person holds the shares — decision 47.
 *
 * Both are ordinary. An individual is identified by personnummer, a company by
 * organisasjonsnummer, and **everything downstream is identical**: purchases,
 * transfers, exercises, ownership percentage, the Tegningsliste, the minutes.
 * Options are granted to both, so a corporate option holder is normal.
 *
 * Only two things differ, and they are the two this type carries: which
 * identifier the record holds, and what it may be linked to. A company cannot be
 * a contact on itself; a person has no organisation number.
 */
export type HolderKind = 'person' | 'company'

/** Collection: `skidaCrmShareholders`. */
export interface Shareholder {
  /** Where it is, and how precisely — see `GeoPoint`. Written by the geocoder. */
  geo?: GeoPoint
  id: string
  kind: HolderKind
  /** A person's name, or a company's legal name. */
  name: string
  address: string
  phone: string
  email: string
  /**
   * Eleven digits, no separator. Persons only.
   *
   * Admin-only by the collection's rules, masked to the last four everywhere
   * outside the record itself.
   */
  personnummer: string
  /**
   * Nine digits. Companies only, and **not masked** — it is public information
   * from Enhetsregisteret, and hiding a public number teaches the reader that
   * the masking elsewhere is decoration.
   */
  orgNumber: string
  purchases: SharePurchase[]
  options: OptionGrant[]
  /**
   * Interest in rounds that have not closed. Counts towards nothing.
   *
   * Kept apart from `purchases` so the arithmetic cannot be tempted: a promise
   * of 150 shares in an open round must never appear in a holding, a percentage
   * or shares outstanding, and the surest way to guarantee that is for it not to
   * be in the array those are summed from.
   */
  subscriptions?: Subscription[]
  /**
   * ---- What the aksjeeierbok holds beyond a name ----
   *
   * These come from the register Bjarte keeps for Foretaksregisteret and
   * Folkeinvest, and they are here because a share register is an identity
   * document as much as a ledger. None of them is derived and none affects an
   * arithmetic.
   */
  /** The name as Foretaksregisteret spells it, where it differs from the everyday one. */
  legalName?: string
  /** A second address the person is known by. Not a second identity. */
  altEmail?: string
  /** The human to write to at a corporate holder. */
  contactPerson?: string
  postalCode?: string
  city?: string
  country?: string
  /** Year and place of birth, as the source records them for a person. */
  birthNote?: string
  /** The share numbers this holding covers, e.g. `17106–30000`. */
  shareSeries?: string
  /** ISO date they first came onto the register. */
  entryDate?: string
  vpsAccount?: string
  /** Folkeinvest's own reference, so a row can be traced back to their file. */
  folkeinvestRef?: string
  /** Other spellings the same holder appears under across the sources. */
  nameVariants?: string
  /** Which documents this row was assembled from. Provenance, not decoration. */
  sources?: string
  /** Where else they appear in Skida — see `ShareholderMatches`. */
  matches?: ShareholderMatches
  /** Firmaattest, signed subscription form, transfer agreement, board consent. */
  attachments?: Attachment[]
  /**
   * ---- Also in the CRM ----
   *
   * Which of these may be set follows the kind. A **company** links to the lead
   * itself; a **person** links to a contact on a lead, to an ambassador, or to a
   * member of the team, because employees hold shares too.
   *
   * Every one keys on an id rather than a name, so a rename cannot orphan a
   * link — `linkedLabel` is the copy kept for display, as on a task.
   */
  linkedLeadId: string
  /** `Contact.id` on that lead, when the holder is the person rather than the firm. */
  linkedLeadContactId: string
  /** Kept for the row when the target is gone. */
  linkedLeadContact: string
  /** `Person.id` in `skidaCrmPeople`, when this is one of our ambassadors. */
  linkedAmbassadorId: string
  /** Account email in `skidaCrmUsers`, when this is one of us. */
  linkedTeamEmail: string
  /** The linked target's name as it read when the link was made, kept for the row when it is gone. */
  linkedLabel: string
  createdAt?: string
  updatedAt?: string
}

/**
 * What a row says the holder is, under its name.
 *
 * A person's line is the word alone. Decision 50 corrected 48 here: a masked
 * personnummer is still a personnummer in a list view, and decision 14 had
 * already ruled that out — the masking was real, but the rule is not about
 * legibility. The kind stays visible at a glance, which is what the line is for;
 * the identifier does not, which is what 14 is for.
 *
 * A company keeps its organisation number, because that is public information
 * from Enhetsregisteret and hiding it would teach the reader that the other
 * masking is decoration.
 */
export function holderKindLabel(s: Shareholder): string {
  if (s.kind === 'company') {
    return s.orgNumber ? `Company · org.nr ${formatOrgNumber(s.orgNumber)}` : 'Company'
  }
  return 'Person'
}

export const EMPTY_SHAREHOLDER: Omit<Shareholder, 'id'> = {
  // A person unless somebody says otherwise: the identifier that needs the most
  // care is the one on the default, so nothing is ever entered as a company by
  // accident and given a personnummer field to fill.
  kind: 'person',
  name: '',
  address: '',
  phone: '',
  email: '',
  personnummer: '',
  orgNumber: '',
  purchases: [],
  options: [],
  linkedLeadId: '',
  linkedLeadContactId: '',
  linkedLeadContact: '',
  linkedAmbassadorId: '',
  linkedTeamEmail: '',
  linkedLabel: '',
}

/**
 * One message this system sent to one shareholder.
 *
 * Collection: `skidaCrmShareholderMail`, written only by the send function and
 * updated only by the delivery webhook — see `firestore.rules`. It answers "what
 * has this investor been sent, and did it arrive", and deliberately not "did
 * they read it": opens are counted per send and never per person.
 */
export interface ShareholderMail {
  id: string
  shareholderId: string
  /** The send this went out with — how the log groups a message back to its batch. */
  sendId: string
  /** The holder's name as it was when the message left. */
  name: string
  /** `payment-request`, or `shareholder-mail` for a letter to a group. */
  kind: string
  /** Which recipient group put them in this send, where one did. */
  group: string
  /** Merged as that person received it. */
  subject: string
  issuanceId: string
  issuanceName: string
  /** The address it went to, which may since have changed on the record. */
  email: string
  shares: number
  amount: number
  sentAt: string
  sentBy: string
  /** `sent` until the webhook hears otherwise. */
  status: 'sent' | 'delivered' | 'delayed' | 'bounced' | 'complained'
  /** Why it failed, when it did. */
  failure?: string
  /**
   * When the message was first fetched by a mail client, and how often since.
   *
   * Decision 32: recorded for investor mail and not for the newsletter, because
   * "did they see the payment request" is the question before chasing somebody.
   * A ceiling rather than a count — Apple Mail Privacy Protection fetches the
   * pixel whether or not a person looked, so this means *fetched*, not *read*.
   */
  openedAt?: string
  opens?: number
  clicked?: boolean
}

/**
 * One send to the register — the row the mail log lists.
 *
 * Collection: `skidaCrmEmailSends`, shared with the newsletter and filtered to
 * the sends this screen is about by `audience`. Written only by the send
 * functions; the per-recipient rows beside it are `ShareholderMail`.
 *
 * The counts here are what the send function handed to Resend. What became of
 * each message afterwards lives on the recipient rows, because delivery is a
 * fact about one message and not about a batch — a send of forty where two
 * bounced is not "a failed send", and a total that rolled the two up would say
 * so.
 */
export interface ShareholderSend {
  id: string
  subject: string
  /** The body, so a draft can be reopened where it was left. */
  html: string
  /** The holder statuses this went to. Empty on the older payment requests. */
  groups: HolderStatus[]
  issuanceId: string
  issuanceName: string
  /** `shareholders` for a group letter, `issuance-subscribers` for a request. */
  audience: string
  total: number
  sent: number
  failed: number
  attachmentCount: number
  /** `draft` until it is sent; then `sending`, `finished`, or with errors. */
  status: string
  /** Absent on a draft — nothing has started. */
  startedAt: string
  startedBy: string
  /** When the draft was last saved, for the row that has no send date. */
  updatedAt: string
  /** When it finished sending. Empty on a draft. */
  finishedAt: string
  /** A resend to non-openers: the letter it resent. 23 Oct 2026. */
  resendOf: string
  /** On the original: the resend made from it, and when. */
  resentAs: string
  resentAt: string
}

/** A draft has never gone out, so it has no recipients and no figures. */
export const isDraft = (s: ShareholderSend) => s.status === 'draft'

export const MAIL_STATES: Record<ShareholderMail['status'], { label: string; tone: string }> = {
  sent: { label: 'Sent', tone: 'muted' },
  delivered: { label: 'Delivered', tone: 'ok' },
  delayed: { label: 'Delayed', tone: 'warn' },
  bounced: { label: 'Bounced', tone: 'bad' },
  complained: { label: 'Marked as spam', tone: 'bad' },
}

export type HolderStatus = 'shareholder' | 'optionholder' | 'potential' | 'former'

export const HOLDER_STATUSES: {
  key: HolderStatus
  label: string
  color: string
  bg: string
}[] = [
  { key: 'potential', label: 'Potential investor', color: '#6b7280', bg: '#f1f2f3' },
  { key: 'optionholder', label: 'Option holder', color: '#3456da', bg: '#eaefff' },
  { key: 'shareholder', label: 'Shareholder', color: '#3aa52f', bg: '#eaf7e7' },
  // Sold out or bought out, and still on the register. Slate rather than the
  // grey of a potential investor: somebody who has held shares is a different
  // thing from somebody who never has, and the register keeps both.
  { key: 'former', label: 'Former shareholder', color: '#475569', bg: '#eef1f5' },
]

export const holderStatusStyle = (s: HolderStatus) =>
  HOLDER_STATUSES.find((x) => x.key === s) ?? HOLDER_STATUSES[0]!

/* -------------------------------------------------------------------------- */
/*  The three ledgers                                                          */
/* -------------------------------------------------------------------------- */

/** Shares the company issued to this holder in a round. */
export const purchasedShares = (s: Shareholder): number =>
  s.purchases.reduce((n, p) => n + (p.shares || 0), 0)

/**
 * Shares that came from a grant being exercised **and paid for**.
 *
 * These are real shares from the day the strike price lands: they count in the
 * holding, in the percentage and in the total outstanding, which is what makes
 * an option holder who exercises become a shareholder without anybody typing a
 * purchase in. Exercised-but-unpaid counts nowhere, and neither does expired.
 */
export const exercisedShares = (s: Shareholder): number =>
  s.options.reduce(
    (n, o) => n + (o.status === 'exercised' && o.paidDate ? o.count || 0 : 0),
    0,
  )

/** Exercised, awaiting the money. Shown on the record, in no total. */
export const unpaidExerciseShares = (s: Shareholder): number =>
  s.options.reduce((n, o) => n + (isUnpaidExercise(o) ? o.count || 0 : 0), 0)

/** Everything the company put in this holder's hands, before any sale. */
export const issuedShares = (s: Shareholder): number =>
  purchasedShares(s) + exercisedShares(s)

/** Options still outstanding as options — the only ones the totals count. */
export const activeOptions = (s: Shareholder): number =>
  s.options.reduce((n, o) => n + (o.status === 'active' ? o.count || 0 : 0), 0)

/** The full life of this holder's grants, for the record's own summary. */
export function optionSplit(s: Shareholder): {
  active: number
  exercised: number
  unpaid: number
  expired: number
} {
  return {
    active: activeOptions(s),
    exercised: exercisedShares(s),
    unpaid: unpaidExerciseShares(s),
    expired: s.options.reduce((n, o) => n + (o.status === 'expired' ? o.count || 0 : 0), 0),
  }
}

/**
 * Settled sales out of one holder. A pending transfer has moved nothing.
 *
 * A buy-back is a sale like any other from the seller's side — the shares leave
 * them either way, and what makes it a buy-back is only where they went.
 */
export const transferredOut = (id: string, transfers: ShareTransfer[]): number =>
  transfers.reduce((n, t) => n + (t.settled && t.fromId === id ? t.shares || 0 : 0), 0)

/** A buy-back has no buyer, so nothing here ever matches one. */
export const transferredIn = (id: string, transfers: ShareTransfer[]): number =>
  transfers.reduce(
    (n, t) => n + (t.settled && !t.buyback && t.toId === id ? t.shares || 0 : 0),
    0,
  )

/** Shares the company has bought back and retired, settled only. */
export const boughtBack = (transfers: ShareTransfer[]): number =>
  transfers.reduce((n, t) => n + (t.settled && t.buyback ? t.shares || 0 : 0), 0)

/** What this holder actually holds today: issued, less sold, plus bought. */
export const netShares = (s: Shareholder, transfers: ShareTransfer[]): number =>
  issuedShares(s) - transferredOut(s.id, transfers) + transferredIn(s.id, transfers)

/**
 * Shares outstanding — the denominator every percentage on the screen uses.
 *
 * Sums what was issued, less what the company has bought back. Never what is
 * held: a transfer moves shares between two holders and both sides are inside
 * this sum, so counting held shares would give the same answer on a good day and
 * a different one the moment a transfer names somebody who is no longer on the
 * register.
 */
export const sharesOutstanding = (
  holders: Shareholder[],
  transfers: ShareTransfer[] = [],
): number => holders.reduce((n, s) => n + issuedShares(s), 0) - boughtBack(transfers)

/** Whether this person has ever held a share, however briefly. */
export const everHeld = (s: Shareholder, transfers: ShareTransfer[]): boolean =>
  issuedShares(s) > 0 || transferredIn(s.id, transfers) > 0

/**
 * Status is derived, never stored.
 *
 * Holding shares makes you a shareholder; holding only active options makes you
 * an option holder; holding nothing after having held something makes you a
 * former one; on the register with neither and never having had either, you are
 * somebody who has not bought yet.
 *
 * The fourth state is the 14 Aug round's, and it is the point of a register:
 * somebody who sold out is not "a potential investor" — they are history, and
 * the aksjeeierbok keeps history. Storing any of this would let it disagree with
 * the numbers beside it the first time a purchase is recorded, or the first time
 * somebody sells out.
 */
export function holderStatus(s: Shareholder, transfers: ShareTransfer[]): HolderStatus {
  if (netShares(s, transfers) > 0) return 'shareholder'
  if (everHeld(s, transfers)) return 'former'
  if (activeOptions(s) > 0) return 'optionholder'
  return 'potential'
}

/** One holder's derived position — computed once per render, read everywhere. */
export interface Holding {
  /** Held today, after settled transfers. */
  shares: number
  /** Issued to them, before any sale. */
  issued: number
  /** Active grants only. */
  options: number
  /** Share of the company, or null when nobody holds anything yet. */
  pct: number | null
  status: HolderStatus
}

/**
 * Every holder's position, keyed by id.
 *
 * One pass over the register rather than a `netShares` call per cell: the table
 * asks for shares, percentage and status on every row, and each of those walks
 * the transfer list.
 */
export function holdings(
  holders: Shareholder[],
  transfers: ShareTransfer[],
): Map<string, Holding> {
  const outstanding = sharesOutstanding(holders, transfers)
  const map = new Map<string, Holding>()
  for (const s of holders) {
    const shares = netShares(s, transfers)
    map.set(s.id, {
      shares,
      issued: issuedShares(s),
      options: activeOptions(s),
      pct: outstanding > 0 && shares > 0 ? (shares / outstanding) * 100 : null,
      status: holderStatus(s, transfers),
    })
  }
  return map
}

export const EMPTY_HOLDING: Holding = {
  shares: 0,
  issued: 0,
  options: 0,
  pct: null,
  status: 'potential',
}

/**
 * How many shares a seller may put into this transfer.
 *
 * Their net holding, plus — when an already-settled transfer is being edited —
 * the shares that transfer itself took out of it. Without that second term,
 * opening a settled 3,000-share transfer and pressing Save unchanged would fail
 * validation against a holding those very shares had already left.
 */
export function sellerCapacity(
  seller: Shareholder,
  transfers: ShareTransfer[],
  editingId: string | null,
): number {
  const editing = editingId ? transfers.find((t) => t.id === editingId) : undefined
  const back = editing && editing.settled && editing.fromId === seller.id ? editing.shares : 0
  return netShares(seller, transfers) + back
}

/**
 * What one issuance actually raised.
 *
 * Two things are subscribed into an increase and they are priced differently:
 * ordinary purchases at the round's price per share, and **paid option
 * exercises booked against it** at the grant's own strike. The board minutes
 * call that second column *herav opsjonskurs*, and totalling both at the round
 * price would overstate what was paid by the difference between them.
 *
 * An unpaid exercise is not in here for the same reason it is in no other
 * total: the money has not arrived. Buy-backs are not either — they reduce
 * shares outstanding, not capital raised, and they are somebody else's money
 * going out rather than the company's coming in.
 */
export function issuanceTotals(iss: Issuance, holders: Shareholder[]) {
  let shares = 0
  let optionShares = 0
  let raised = 0
  let priced = false
  const investors = new Set<string>()
  for (const h of holders) {
    for (const p of h.purchases) {
      if (p.issuanceId !== iss.id) continue
      shares += p.shares || 0
      investors.add(h.id)
      // The holder's own price where one was agreed, the round's otherwise.
      const price = effectivePrice(p, iss)
      if (price !== null) {
        raised += (p.shares || 0) * price
        priced = true
      }
    }
    for (const o of h.options) {
      if (o.status !== 'exercised' || !o.paidDate || o.exerciseIssuanceId !== iss.id) continue
      shares += o.count || 0
      optionShares += o.count || 0
      investors.add(h.id)
      if (o.strikePrice !== null) {
        raised += (o.count || 0) * o.strikePrice
        priced = true
      }
    }
  }
  return {
    shares,
    /** Of those, shares that came from an exercised grant. */
    optionShares,
    // Null rather than nought when nothing in the round carries a price: a zero
    // reads as a round that raised nothing.
    raised: priced ? raised : null,
    investors: investors.size,
  }
}

/**
 * How many holders in this round pay something other than the round's price.
 *
 * Decision 78: *"the card says a round is mixed ('2 subscribers at agreed
 * prices') rather than showing one price and being quietly wrong about three
 * rows."* The card has room for one price and a round can carry several, so the
 * honest card is the one that admits it.
 *
 * Counted over both ledgers, because a round is mixed whether the shares exist
 * yet or not: Emisjon 4's three prices are on its *subscriptions*, and a card
 * that only looked at purchases would call the one round this exists for
 * single-priced.
 */
export function agreedPriceHolders(iss: Issuance, holders: Shareholder[]): number {
  const named = new Set<string>()
  for (const h of holders) {
    const lines: { pricePerShare?: number | null }[] = [
      ...h.purchases.filter((p) => p.issuanceId === iss.id),
      ...(h.subscriptions ?? []).filter((x) => x.issuanceId === iss.id),
    ]
    // An agreed price is one recorded on the holder's own line. A line that
    // simply repeats the round's price is not an exception and must not be
    // counted as one, which is why this reads `pricePerShare` rather than
    // comparing `effectivePrice` to the round.
    if (lines.some((l) => l.pricePerShare !== null && l.pricePerShare !== undefined)) {
      named.add(h.id)
    }
  }
  return named.size
}

/** One holder's unpaid position in a round — see `outstandingIn`. */
export interface OutstandingRow {
  holderId: string
  holderName: string
  email: string
  phone: string
  shares: number
  /** What they owe: their own price where one was agreed, the round's otherwise. */
  amount: number
  /** Whether that price was agreed with them rather than being the round's. */
  agreed: boolean
}

/**
 * Who has not paid for a round, and how much is outstanding — 28 Aug 2026.
 *
 * The payment request could always say who had been *asked*. Nothing said who
 * had *paid*, so reconciling Emisjon 4 meant a bank statement open beside the
 * screen and thirty-eight names matched by eye. `paidAt` on a purchase is the
 * missing half and this is the reading of it.
 *
 * **Only purchases count.** Interest in a round that has not closed is a promise
 * and owes nothing yet — it is not in the array this sums, for the same reason
 * it is in no holding.
 *
 * A holder appears once with everything they owe in that round summed, because
 * that is what an invoice would say. A holder who has paid some lines and not
 * others appears for the unpaid remainder — which is the honest figure and the
 * one worth chasing.
 */
export function outstandingIn(
  iss: Issuance,
  holders: Shareholder[],
): { rows: OutstandingRow[]; total: number; paidCount: number; paidTotal: number } {
  const rows: OutstandingRow[] = []
  let paidCount = 0
  let paidTotal = 0

  for (const h of holders) {
    const mine = h.purchases.filter((p) => p.issuanceId === iss.id)
    if (mine.length === 0) continue

    let owed = 0
    let shares = 0
    let agreed = false
    let settled = 0
    for (const p of mine) {
      const price = effectivePrice(p, iss)
      const amount = price === null ? 0 : (p.shares || 0) * price
      if (p.paidAt) {
        settled += amount
        continue
      }
      owed += amount
      shares += p.shares || 0
      if (p.pricePerShare !== null && p.pricePerShare !== undefined) agreed = true
    }
    paidTotal += settled
    // Counted as paid only when nothing of theirs is left, so a holder who has
    // settled one line of two is not reported as done.
    if (owed === 0) paidCount++
    else {
      rows.push({
        holderId: h.id,
        holderName: h.name,
        email: h.email,
        phone: h.phone,
        shares,
        amount: owed,
        agreed,
      })
    }
  }

  // Largest first: chasing is done in order of what it is worth.
  rows.sort((a, b) => b.amount - a.amount || a.holderName.localeCompare(b.holderName))
  return { rows, total: rows.reduce((n, r) => n + r.amount, 0), paidCount, paidTotal }
}

/** One line of the tegningsliste as it stands — decision 82. */
export interface InterestRow {
  holderId: string
  holderName: string
  shares: number
  /** What this subscriber pays: their own price, or the round's. */
  price: number | null
  /** `shares × price`, or null where no price is recorded anywhere yet. */
  amount: number | null
  /** Whether that price was agreed with them rather than being the round's. */
  agreed: boolean
  /** The source's own words, verbatim — the whole point of the list. */
  note: string
}

/**
 * The interest against an open round, as its own list — decision 82.
 *
 * *"Forty names at four prices is a subscription list; a card caption cannot
 * hold it, and flattening 'registered interest' and 'signed subscription form,
 * kurs kr 30,50' into one status pill loses the difference between a maybe and a
 * commitment."* So the note travels untouched and the screen shows it as written.
 *
 * Ordered by shares, largest first — a tegningsliste is read to see who is
 * taking the most — with the name as the tie-break so the order is stable
 * between renders.
 */
export function interestRows(iss: Issuance, holders: Shareholder[]): InterestRow[] {
  const rows: InterestRow[] = []
  for (const h of holders) {
    for (const x of h.subscriptions ?? []) {
      if (x.issuanceId !== iss.id) continue
      const price = effectivePrice(x, iss)
      rows.push({
        holderId: h.id,
        holderName: h.name,
        shares: x.shares,
        price,
        amount: price === null ? null : x.shares * price,
        agreed: x.pricePerShare !== null && x.pricePerShare !== undefined,
        note: x.note,
      })
    }
  }
  return rows.sort((a, b) => b.shares - a.shares || a.holderName.localeCompare(b.holderName))
}

/**
 * What a grant does not say — decision 80.
 *
 * The 2 191 options the register inherited carry a count and nothing else: no
 * strike, no grant date, no expiry. The row has to name which of the three are
 * missing rather than showing `kr 0,00` and an empty date, because a nought
 * strike is a price somebody agreed and an empty date field looks like a value
 * that failed to load. *"The record looking unfinished is correct — it is."*
 *
 * Returns an empty array where the grant is complete, so the caller can render
 * on truthiness.
 */
export function missingGrantTerms(o: OptionGrant): string[] {
  const gaps: string[] = []
  if (o.strikePrice === null || o.strikePrice === undefined) gaps.push('no strike')
  if (!o.grantDate) gaps.push('no grant date')
  if (!o.expiryDate) gaps.push('no expiry')
  return gaps
}

/**
 * What an exercised grant is missing from its own paperwork — 28 Aug 2026.
 *
 * An exercise is a share capital increase (decision 16), so it has to say which
 * increase it belongs to and under what resolution. Eight grants carry neither
 * the resolution nor a way to have got one: `closeIssuance` exercised them on
 * 19 Aug from the option programme, and the board reference was not in the
 * source it read.
 *
 * This is a **finding, not a block.** It used to be enforced at save time, which
 * meant those eight holders could not be edited at all — changing an entry date
 * failed on a grant the person had not touched. And the check could never catch
 * a live mistake: `bookExercise` already refuses without both, and Undo returns
 * the grant to `active` and clears them, so the UI cannot produce this shape. It
 * only ever fired on data that arrived from a script.
 *
 * So it reads like decision 80's missing terms, which is the same situation and
 * already has an answer: name the gap on the row, let the record save, and let
 * the register look as unfinished as it is.
 */
export function exerciseGapNote(o: OptionGrant): string {
  if (o.status !== 'exercised') return ''
  const gaps: string[] = []
  if (!o.exerciseIssuanceId) gaps.push('which increase it was booked against')
  if (!o.exerciseResolution) gaps.push('the board or AGM resolution that allowed it')
  if (gaps.length === 0) return ''
  return `Exercised without ${gaps.join(' or ')} on record. An exercise is a capital increase and a filing needs both — add it before the next one.`
}

/** The sentence 80 asks for, or an empty string where the terms are recorded. */
export function grantTermsNote(o: OptionGrant): string {
  const gaps = missingGrantTerms(o)
  if (gaps.length === 0) return ''
  return `Terms not recorded — ${gaps.join(', ')} in the source. Add them before the next filing.`
}

/** Digits only — what gets stored, whatever separators were typed. */
export const normalisePersonnummer = (v: string): string => v.replace(/\D/g, '')

/**
 * The value to store, for any country's identifier.
 *
 * Digits only **when the result is a Norwegian personnummer**, and the source's
 * own text otherwise. Stripping the non-digits from `FDRGST68M22H501Z` or
 * `759-229-578 (Canada)` destroys them silently, which is the worst way to lose
 * an identity document.
 *
 * `mapShareholder` has applied exactly this on the way out of Firestore since
 * the foreign holders arrived. It is here so the form can apply the same rule on
 * the way in — it used to store the stripped value, so a foreign identifier was
 * quietly mangled by the act of opening a record and pressing Save.
 */
export function storedPersonnummer(v: string): string {
  const raw = v.trim()
  const digits = normalisePersonnummer(raw)
  return digits.length === 11 ? digits : raw
}

/**
 * `924 118 292`, as Enhetsregisteret writes it.
 *
 * Not masked anywhere, unlike the personnummer beside it. An organisation number
 * is public, and obscuring a public number would teach the reader that the
 * masking on the other one is decoration rather than a rule.
 */
export function formatOrgNumber(v: string): string {
  const d = v.replace(/\D/g, '')
  return d.length === 9 ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}` : d
}

/**
 * Last four characters, the rest masked — the only form a list ever shows.
 *
 * Masks the value **as recorded**, not its digits. Stripping to digits first
 * turned an Italian codice fiscale — `FDRGST68M22H501Z` — into a mask of the
 * wrong four characters, and a Canadian `759-229-578` into something that was
 * not what anybody would recognise. The register holds fourteen foreign holders
 * and the identifier is theirs, whatever shape their country writes it in.
 */
export function maskPersonnummer(v: string): string {
  const raw = v.trim()
  if (!raw) return ''
  return raw.length <= 4 ? raw : `••••••${raw.slice(-4)}`
}

/**
 * `DDMMYY NNNNN`, as the register is written on paper.
 *
 * No control-digit check, and **no length check either**. A fødselsnummer has a
 * control digit and so does a D-number, but the rules differ by kind — and none
 * of it applies to the fourteen foreign holders on this register, whose
 * identifiers are ten digits, twelve digits, or sixteen characters with letters
 * in them. A validator that rejects a real person is worse than one that accepts
 * a typo.
 *
 * So the Norwegian spacing is applied only when the value *is* a Norwegian
 * eleven-digit number, and anything else is shown exactly as it was recorded.
 * Reformatting a codice fiscale into `DDMMYY NNNNN` would be inventing a shape
 * its own country does not use.
 */
export function formatPersonnummer(v: string): string {
  const raw = v.trim()
  const d = normalisePersonnummer(raw)
  return d.length === 11 && d === raw ? `${d.slice(0, 6)} ${d.slice(6)}` : raw
}
